import {
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
} from "@material-ui/core";
import { AxiosInstance } from "axios";
import React from "react";
import {
  Button,
  ResourceContextProvider,
  useNotify,
  Error as RaError,
} from "react-admin";
import { Controller, useForm, useWatch } from "react-hook-form";
import { RouteComponentProps, useLocation } from "react-router-dom";
import resourceNames from "../../../../common/data/resourceNames";
import { useAppData } from "../../../../common/hooks/useAppData";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { CustomResourceRouteParams } from "../../../../common/types";
import { CompactFormToolbar } from "../../../common/form/CompactFormToolbar";
import { EditActions } from "../../../common/form/FormActions";
import SaveIcon from "@material-ui/icons/Save";
import "./DriverLogExceptionAdd.css";
import { HosLogsApiFactory } from "../../../../common/data/service-proxies";

interface Props extends RouteComponentProps<CustomResourceRouteParams> {
  onClose: () => void;
  onSuccess: () => void;
}

type FormValues = {
  driverId?: number;
  logDate?: string;
  country: string;
  exceptionId: number;
};

const onSubmit = (
  data: FormValues,
  axios: AxiosInstance | null,
  onSuccess: () => void
) => {
  if (!axios) {
    onSuccess();
    return;
  }

  HosLogsApiFactory(undefined, axios.defaults.baseURL, axios)
    .apiAppHoslogsAddexceptionPost(data)
    .then(() => {
      onSuccess();
    })
    .catch((e) => {
      console.error(e);
    });
};

export const DriverLogExceptionAdd = ({ onSuccess, onClose }: Props) => {
  const notify = useNotify();
  const axios = useAxiosInstance();
  const countriesAndStates = useAppData((x) => x.countries) ?? [];
  const countries = countriesAndStates.map((x) => ({
    id: x.id!,
    name: x.name!,
  }));
  const allExceptions = useAppData((x) => x.hosExceptions) ?? {};

  const location = useLocation<
    | {
        record?: { driverId?: number; date?: string };
      }
    | undefined
  >();

  let record: { driverId?: number; date?: string };
  let driverId: number | undefined;
  let logDate: string | undefined;
  if (location.state?.record) {
    record = location.state.record;
    driverId = record.driverId;
    logDate = record.date;
  }

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      driverId,
      logDate,
    },
    mode: "onBlur",
  });

  const selectedCountry = useWatch({
    control,
    name: `country`,
  });

  const exceptions = allExceptions[selectedCountry] ?? [];

  return (
    <ResourceContextProvider value={`${resourceNames.HOS_EVENTS}`}>
      <div id="driver-log-add-exception-container">
        <section>
          <EditActions
            basePath={`/${resourceNames.HOS_DRIVER_LOG_VIEW}/${driverId}/${logDate}`}
            onClose={onClose}
          />
        </section>
        <section>
          <Paper>
            <div className="form-create-edit-drawer">
              {!!driverId && !!logDate ? (
                <form
                  className="simple-form"
                  onSubmit={handleSubmit(
                    (data) => onSubmit(data, axios, onSuccess),
                    () => notify("resources.common.labels.invalidForm", "error")
                  )}>
                  <CardContent>
                    <div className="ra-input">
                      <Controller
                        name={`country`}
                        control={control}
                        defaultValue={""}
                        rules={{ required: true }}
                        render={(props) => (
                          <FormControl
                            variant="outlined"
                            margin="dense"
                            required>
                            <InputLabel id="country-label">Country</InputLabel>
                            <Select
                              labelId="country-label"
                              label="Country"
                              {...props.field}
                              error={!!props.fieldState.error}>
                              {countries.map((x) => (
                                <MenuItem key={x.id} value={`${x.id}`}>
                                  {x.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </div>
                    <div className="ra-input">
                      <Controller
                        name={`exceptionId`}
                        control={control}
                        defaultValue={"" as any}
                        rules={{ required: true }}
                        render={(props) => (
                          <FormControl
                            variant="outlined"
                            margin="dense"
                            required
                            className="ra-input">
                            <InputLabel id="exception-label">
                              Exception
                            </InputLabel>
                            <Select
                              labelId="exception-label"
                              label="Exception"
                              {...props.field}
                              error={!!props.fieldState.error}>
                              {!!exceptions
                                ? exceptions.map((x) => (
                                    <MenuItem key={x.id} value={x.id!}>
                                      {x.name}
                                    </MenuItem>
                                  ))
                                : null}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </div>
                  </CardContent>
                  <CompactFormToolbar>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      label="ra.action.save">
                      <SaveIcon />
                    </Button>
                  </CompactFormToolbar>
                </form>
              ) : (
                <RaError
                  error={
                    new Error(
                      "Unexpected error, please refresh the page and try again"
                    )
                  }
                />
              )}
            </div>
          </Paper>
        </section>
      </div>
    </ResourceContextProvider>
  );
};
